import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface ApplicationState {
  applicationId: string | null;
  applicationToken: string | null;
  phone: string;
  phoneVerified: boolean;
  phoneToken: string | null;
  currentStep: number;
  completedSteps: number[];
  submitted: boolean;
  applicationNumber: string | null;
}

interface ApplicationContextType {
  state: ApplicationState;
  setApplication: (id: string, token: string) => void;
  setPhoneVerified: (phone: string, token: string) => void;
  setCurrentStep: (step: number) => void;
  completeStep: (step: number) => void;
  setSubmitted: (number: string) => void;
  clearApplication: () => void;
  updateField: (field: string, value: unknown) => void;
}

const initialState: ApplicationState = {
  applicationId: null,
  applicationToken: null,
  phone: '',
  phoneVerified: false,
  phoneToken: null,
  currentStep: 1,
  completedSteps: [],
  submitted: false,
  applicationNumber: null,
};

const ApplicationContext = createContext<ApplicationContextType | null>(null);

export function ApplicationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ApplicationState>(() => {
    const saved = sessionStorage.getItem('app_state');
    return saved ? { ...initialState, ...JSON.parse(saved) } : initialState;
  });

  const saveState = useCallback((newState: ApplicationState) => {
    setState(newState);
    sessionStorage.setItem('app_state', JSON.stringify(newState));
  }, []);

  const setApplication = useCallback((id: string, token: string) => {
    saveState({ ...state, applicationId: id, applicationToken: token });
  }, [state, saveState]);

  const setPhoneVerified = useCallback((phone: string, token: string) => {
    saveState({ ...state, phone, phoneVerified: true, phoneToken: token, currentStep: 2, completedSteps: [...new Set([...state.completedSteps, 1])] });
  }, [state, saveState]);

  const setCurrentStep = useCallback((step: number) => {
    saveState({ ...state, currentStep: step });
  }, [state, saveState]);

  const completeStep = useCallback((step: number) => {
    saveState({ ...state, completedSteps: [...new Set([...state.completedSteps, step])] });
  }, [state, saveState]);

  const setSubmitted = useCallback((number: string) => {
    saveState({ ...state, submitted: true, applicationNumber: number, currentStep: 7 });
  }, [state, saveState]);

  const clearApplication = useCallback(() => {
    sessionStorage.removeItem('app_state');
    setState(initialState);
  }, []);

  const updateField = useCallback((_field: string, _value: unknown) => {
    // Used by autosave hook
  }, []);

  return (
    <ApplicationContext.Provider value={{
      state,
      setApplication,
      setPhoneVerified,
      setCurrentStep,
      completeStep,
      setSubmitted,
      clearApplication,
      updateField,
    }}>
      {children}
    </ApplicationContext.Provider>
  );
}

export function useApplication() {
  const ctx = useContext(ApplicationContext);
  if (!ctx) throw new Error('useApplication must be used within ApplicationProvider');
  return ctx;
}
